<script setup lang="ts">
import { AlertTriangle, Check, Copy, Info, ShieldAlert } from "@lucide/vue";
import { onBeforeUnmount, ref } from "vue";
import type { ContentDocument } from "../types/content";

defineProps<{ document: ContentDocument }>();

const calloutIcons = { note: Info, warning: AlertTriangle, security: ShieldAlert };
const copiedIndex = ref<number | null>(null);
let copiedTimer: number | undefined;

async function copyCode(code: string, index: number) {
  try {
    await navigator.clipboard.writeText(code);
    copiedIndex.value = index;
    window.clearTimeout(copiedTimer);
    copiedTimer = window.setTimeout(() => { copiedIndex.value = null; }, 1800);
  } catch {
    copiedIndex.value = null;
  }
}

onBeforeUnmount(() => window.clearTimeout(copiedTimer));
</script>

<template>
  <div class="document-body">
    <template v-for="(block, index) in document.blocks" :key="`${block.type}-${index}`">
      <component :is="`h${block.level}`" v-if="block.type === 'heading'" :id="block.id">{{ block.text }}</component>
      <p v-else-if="block.type === 'paragraph'">{{ block.text }}</p>
      <component :is="block.style === 'ordered' ? 'ol' : 'ul'" v-else-if="block.type === 'list'" :class="{ 'check-list': block.style === 'check' }">
        <li v-for="item in block.items" :key="item"><Check v-if="block.style === 'check'" :size="17" aria-hidden="true" /> <span>{{ item }}</span></li>
      </component>
      <aside v-else-if="block.type === 'callout'" class="document-callout" :class="`tone-${block.tone}`">
        <component :is="calloutIcons[block.tone]" :size="20" aria-hidden="true" />
        <div><strong>{{ block.title }}</strong><p>{{ block.text }}</p></div>
      </aside>
      <figure v-else-if="block.type === 'code'" class="code-block">
        <figcaption>
          <span>{{ block.caption || block.language.toUpperCase() }}</span>
          <button type="button" :aria-label="copiedIndex === index ? '代码已复制' : '复制代码'" :title="copiedIndex === index ? '已复制' : '复制代码'" @click="copyCode(block.code, index)">
            <Check v-if="copiedIndex === index" :size="16" aria-hidden="true" />
            <Copy v-else :size="16" aria-hidden="true" />
          </button>
        </figcaption>
        <pre><code :class="`language-${block.language}`">{{ block.code }}</code></pre>
      </figure>
      <div v-else-if="block.type === 'table'" class="table-scroll" tabindex="0" role="region" aria-label="数据表格">
        <table><thead><tr><th v-for="column in block.columns" :key="column" scope="col">{{ column }}</th></tr></thead>
          <tbody><tr v-for="(row, rowIndex) in block.rows" :key="rowIndex"><td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell }}</td></tr></tbody>
        </table>
      </div>
    </template>
  </div>
</template>
